import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  chatService,
  CreateChatNoteDto,
  ChatNoteResponseDto,
} from "@/services/chat-service";

export const chatKeys = {
  all: ["chat"] as const,
  notes: () => [...chatKeys.all, "notes"] as const,
  notesByChannel: (channelId: string) =>
    [...chatKeys.notes(), channelId] as const,
};

export const useTranslateMessage = () => {
  return useMutation({
    mutationFn: ({
      messageId,
      targetLanguage,
      translateTranscription = false,
    }: {
      messageId: string;
      targetLanguage?: string;
      translateTranscription?: boolean;
    }) =>
      chatService.translateMessage(
        messageId,
        targetLanguage,
        translateTranscription,
      ),
  });
};

export const useTranscribeMessage = () => {
  return useMutation({
    mutationFn: (messageId: string) => chatService.transcribeMessage(messageId),
  });
};

export const useSuggestResponse = () => {
  return useMutation({
    mutationFn: (channelId: string) =>
      chatService.getSuggestResponse(channelId),
  });
};

export const useChatNotes = (channelId?: string) => {
  return useQuery({
    queryKey: chatKeys.notesByChannel(channelId!),
    queryFn: () => chatService.getChatNotes(channelId!),
    enabled: !!channelId,
  });
};

export const useCreateChatNote = (channelId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateChatNoteDto) =>
      chatService.createChatNote(channelId!, dto),
    onSuccess: (createdNote: ChatNoteResponseDto) => {
      queryClient.setQueryData<ChatNoteResponseDto[]>(
        chatKeys.notesByChannel(channelId!),
        (current = []) => [createdNote, ...current],
      );
      queryClient.invalidateQueries({
        queryKey: chatKeys.notesByChannel(channelId!),
      });
    },
  });
};

export const useDeleteChatNote = (channelId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) =>
      chatService.deleteChatNote(channelId!, noteId),
    onSuccess: (_, deletedNoteId) => {
      queryClient.setQueryData<ChatNoteResponseDto[]>(
        chatKeys.notesByChannel(channelId!),
        (current = []) => current.filter((note) => note.id !== deletedNoteId),
      );
      queryClient.invalidateQueries({
        queryKey: chatKeys.notesByChannel(channelId!),
      });
    },
  });
};
